/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Thu Sep 07 2023
 **/

import { useAtom } from "jotai";
import { atomFamily, atomWithReset, useResetAtom } from "jotai/utils";
import { useCallback, useEffect, useRef } from "react";
import { JotaiStore } from "../commons";
import useDragAndDrop from "./useDragAndDrop";

interface UseFileUploadInterface {
	id: string;
	multiple: boolean;
}

const fileAddAtom = atomWithReset<string[]>([]);
const fileAtomFamily = atomFamily(() => atomWithReset<File[]>([]));

export default function useFileUpload({ id, multiple }: UseFileUploadInterface) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [atomIDList, setAtomIDList] = useAtom(fileAddAtom, { store: JotaiStore });
	const resetFileAddAtom = useResetAtom(fileAddAtom, { store: JotaiStore });
	const fileAtom = fileAtomFamily(id);
	const reset = useResetAtom(fileAtom, { store: JotaiStore });
	const [fileList, setFileList] = useAtom(fileAtom, { store: JotaiStore });
	const drag = useDragAndDrop<HTMLLabelElement>({
		onDrop(event) {
			event.preventDefault();
			const files = Array.from(event.dataTransfer.files) as File[];
			if (multiple) {
				if (files) {
					setFileList((fileList) => [...fileList, ...files]);
				}
			} else {
				setFileList([files[0]]);
			}
			setAtomIDList((prev) => {
				if (prev.includes(id)) {
					return prev;
				} else {
					return [...prev, id];
				}
			});
		}
	});

	const handleChange = (event: Event) => {
		event.preventDefault();
		const target = event.target as HTMLInputElement;
		const files = target.files;
		if (!files) return;
		const fileArray = Array.from(files);

		if (multiple) {
			if (fileArray) {
				setFileList((fileList) => [...fileList, ...fileArray]);
			}
		} else {
			setFileList(fileArray);
		}
		setAtomIDList((prev) => {
			if (prev.includes(id)) {
				return prev;
			} else {
				return [...prev, id];
			}
		});
	};

	const handleCancel = (name: string) => {
		const filteredList = fileList?.filter((file) => file.name !== name);
		setFileList(filteredList);
	};

	useEffect(() => {
		if (!inputRef.current) return;
		inputRef.current.addEventListener("change", handleChange);
		return () => {
			if (!inputRef.current) return;
			inputRef.current.removeEventListener("change", handleChange);
		};
	}, []);

	const removeAll = useCallback(() => {
		atomIDList.forEach((atomID) => {
			fileAtomFamily.remove(atomID);
		});
		resetFileAddAtom();
	}, [atomIDList]);

	return { fileList, inputRef, drag, handleCancel, reset, removeAll };
}
